import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Row,
  Col,
  Select,
  message,
  Modal,
  Form,
  Popconfirm,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { wxMerchantService } from '../../services/wxMerchantService';
import {
  WxMerchantVO,
  WxMerchantQueryRequest,
  WxMerchantSaveRequest,
  Page,
} from '../../types';

const { Option } = Select;
const { Text } = Typography;

const WxMerchantList: React.FC = () => {
  const [list, setList] = useState<WxMerchantVO[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState<Partial<WxMerchantQueryRequest>>({});

  // 新增/编辑弹窗
  const [formVisible, setFormVisible] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState<WxMerchantVO | null>(null);
  const [form] = Form.useForm<WxMerchantSaveRequest>();

  const loadList = useCallback(async (page = 1, pageSize = 10, params?: Partial<WxMerchantQueryRequest>) => {
    setLoading(true);
    try {
      const query: WxMerchantQueryRequest = {
        pageNo: page,
        pageSize,
        ...(params ?? filters),
      };
      const res = await wxMerchantService.getList(query);
      if (res.status === 1) {
        const data = res.data as Page<WxMerchantVO>;
        setList(data.content || []);
        setPagination({ current: page, pageSize, total: data.totalElements || 0 });
      } else {
        message.error(res.msg || '获取商户号列表失败');
      }
    } catch {
      message.error('获取商户号列表失败');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadList(1, 10, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    loadList(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    const empty: Partial<WxMerchantQueryRequest> = {};
    setFilters(empty);
    loadList(1, pagination.pageSize, empty);
  };

  const handleTableChange = (pag: any) => {
    loadList(pag.current, pag.pageSize);
  };

  const openAddModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ status: 1 });
    setFormVisible(true);
  };

  const openEditModal = (record: WxMerchantVO) => {
    setEditingRecord(record);
    form.setFieldsValue({
      mchId: record.mchId,
      mchName: record.mchName,
      mchKey: record.mchKey,
      certPath: record.certPath,
      certKeyPath: record.certKeyPath,
      status: record.status,
      remark: record.remark,
    });
    setFormVisible(true);
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      setFormLoading(true);
      const payload: WxMerchantSaveRequest = { ...values };
      let res;
      if (editingRecord) {
        res = await wxMerchantService.update({ ...payload, id: editingRecord.id });
      } else {
        res = await wxMerchantService.save(payload);
      }
      if (res.status === 1) {
        message.success(editingRecord ? '修改成功' : '新增成功');
        setFormVisible(false);
        loadList(pagination.current, pagination.pageSize);
      }
    } catch {
      // form validation error, ignore
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await wxMerchantService.delete(id);
      if (res.status === 1) {
        message.success('删除成功');
        loadList(pagination.current, pagination.pageSize);
      }
    } catch {
      message.error('删除失败');
    }
  };

  // 脱敏显示 API 密钥
  const maskKey = (key: string) => {
    if (!key || key.length <= 8) return '****';
    return key.slice(0, 4) + '****' + key.slice(-4);
  };

  const columns: ColumnsType<WxMerchantVO> = [
    {
      title: '商户号',
      dataIndex: 'mchId',
      key: 'mchId',
      width: 150,
      render: (text: string) => <Text copyable>{text}</Text>,
    },
    {
      title: '商户名称',
      dataIndex: 'mchName',
      key: 'mchName',
      width: 160,
    },
    {
      title: 'API 密钥',
      dataIndex: 'mchKey',
      key: 'mchKey',
      width: 160,
      render: (text: string) => <Text type="secondary">{maskKey(text)}</Text>,
    },
    {
      title: '证书路径',
      dataIndex: 'certPath',
      key: 'certPath',
      width: 200,
      ellipsis: true,
      render: (text: string) => text || <Text type="secondary">-</Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: number, record: WxMerchantVO) => (
        <Tag color={status === 1 ? 'success' : 'default'}>{record.statusText}</Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (text: string) => text ? text.replace('T', ' ') : '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 130,
      render: (_: any, record: WxMerchantVO) => (
        <Space size={4}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除该商户号吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          商户号列表
          <Tag color="orange">老通道</Tag>
        </Space>
      }
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
          新增商户号
        </Button>
      }
    >
      {/* 筛选区域 */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col>
          <Input
            placeholder="商户名称"
            allowClear
            style={{ width: 180 }}
            value={filters.mchName}
            onChange={e => setFilters(f => ({ ...f, mchName: e.target.value || undefined }))}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Input
            placeholder="商户号"
            allowClear
            style={{ width: 160 }}
            value={filters.mchId}
            onChange={e => setFilters(f => ({ ...f, mchId: e.target.value || undefined }))}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 110 }}
            value={filters.status}
            onChange={v => setFilters(f => ({ ...f, status: v }))}
          >
            <Option value={1}>启用</Option>
            <Option value={0}>禁用</Option>
          </Select>
        </Col>
        <Col>
          <Space>
            <Button type="primary" onClick={handleSearch}>查询</Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Col>
      </Row>

      {/* 数据表格 */}
      <Table<WxMerchantVO>
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        scroll={{ x: 1060 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: total => `共 ${total} 条`,
        }}
        onChange={handleTableChange}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingRecord ? '编辑商户号' : '新增商户号'}
        open={formVisible}
        onOk={handleFormSubmit}
        onCancel={() => setFormVisible(false)}
        confirmLoading={formLoading}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="微信商户号"
                name="mchId"
                rules={[
                  { required: true, message: '请输入微信商户号' },
                  { max: 32, message: '最多32个字符' },
                ]}
              >
                <Input placeholder="请输入微信商户号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="商户名称"
                name="mchName"
                rules={[
                  { required: true, message: '请输入商户名称' },
                  { max: 64, message: '最多64个字符' },
                ]}
              >
                <Input placeholder="请输入商户名称" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="API 密钥"
                name="mchKey"
                rules={[
                  { required: true, message: '请输入API密钥' },
                  { max: 64, message: '最多64个字符' },
                ]}
              >
                <Input.Password placeholder="请输入商户API密钥" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="状态"
                name="status"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  <Option value={1}>启用</Option>
                  <Option value={0}>禁用</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="证书文件路径"
            name="certPath"
            rules={[{ max: 256, message: '最多256个字符' }]}
          >
            <Input placeholder="/cert/apiclient_cert.pem（可选）" />
          </Form.Item>
          <Form.Item
            label="证书密钥路径"
            name="certKeyPath"
            rules={[{ max: 256, message: '最多256个字符' }]}
          >
            <Input placeholder="/cert/apiclient_key.pem（可选）" />
          </Form.Item>
          <Form.Item
            label="备注"
            name="remark"
            rules={[{ max: 255, message: '最多255个字符' }]}
          >
            <Input.TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default WxMerchantList;
